import { z } from 'zod';

export const loginSchema = z.object({
  email: z.string().email('Invalid email address').min(1, 'Email is required'),
  password: z.string().min(8, 'Password must be at least 8 characters')
});

export const csvUploadSchema = z.object({
  file: z.instanceof(File)
    .refine((file) => file.size <= 5 * 1024 * 1024, 'File size must be less than 5MB')
    .refine(
      (file) => ['text/csv', 'application/vnd.ms-excel'].includes(file.type),
      'Only CSV files are allowed'
    )
});

export const xmlUploadSchema = z.object({
  file: z.instanceof(File)
    .refine((file) => file.size <= 10 * 1024 * 1024, 'File size must be less than 10MB')
    .refine(
      (file) => ['text/xml', 'application/xml'].includes(file.type),
      'Only XML files are allowed'
    )
});

export const imageUploadSchema = z.object({
  file: z.instanceof(File)
    .refine((file) => file.size <= 20 * 1024 * 1024, 'File size must be less than 20MB')
    .refine(
      (file) => ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf'].includes(file.type),
      'Only image files (JPEG, PNG, WEBP) or PDF are allowed'
    )
});

export const transactionSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  amount: z.number().positive('Amount must be positive'),
  narration: z.string().min(1, 'Description is required').max(500, 'Description too long')
});

export const invoiceSchema = z.object({
  vendor: z.string().min(1, 'Vendor is required').max(200, 'Vendor name too long'),
  invoiceNo: z.string().min(1, 'Invoice number is required').max(100, 'Invoice number too long'),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  amount: z.number().positive('Amount must be positive')
});

export const matchWeightsSchema = z.object({
  amount: z.number().min(0).max(1),
  date: z.number().min(0).max(1),
  narration: z.number().min(0).max(1)
}).refine(
  (data) => data.amount + data.date + data.narration === 1,
  'Weights must sum to 1.0'
);

export const notificationPreferencesSchema = z.object({
  email_enabled: z.boolean(),
  high_risk_invoices: z.boolean(),
  reconciliation_matches: z.boolean()
});

export type LoginInput = z.infer<typeof loginSchema>;
export type TransactionInput = z.infer<typeof transactionSchema>;
export type InvoiceInput = z.infer<typeof invoiceSchema>;
export type MatchWeightsInput = z.infer<typeof matchWeightsSchema>;
export type NotificationPreferencesInput = z.infer<typeof notificationPreferencesSchema>;
